import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Activity, Dumbbell, Brain, Moon } from "lucide-react";

/* Triple concentric activity ring — blue (course), orange (weekly activity), purple (weeks). */
function TripleRing({ size = 104, completionPct = 0, activitiesPct = 0, weeksPct = 0 }) {
  const stroke = 7, gap = 4;
  const rings = [
    { r: (size - stroke) / 2, pct: completionPct, color: "#22D3EE", id: "r-blue" },
    { r: (size - stroke) / 2 - (stroke + gap), pct: activitiesPct, color: "#F59E0B", id: "r-orange" },
    { r: (size - stroke) / 2 - 2 * (stroke + gap), pct: weeksPct, color: "#A855F7", id: "r-purple" },
  ];
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        {rings.map(({ r, pct, color, id }) => {
          const c = 2 * Math.PI * r;
          const dash = (Math.max(0, Math.min(100, pct)) / 100) * c;
          return (
            <g key={id}>
              <circle cx={size / 2} cy={size / 2} r={r} stroke={`${color}26`} strokeWidth={stroke} fill="none" />
              <motion.circle
                cx={size / 2} cy={size / 2} r={r}
                stroke={color} strokeWidth={stroke} fill="none" strokeLinecap="round"
                strokeDasharray={`${dash} ${c}`}
                style={{ filter: `drop-shadow(0 0 5px ${color})` }}
                initial={{ strokeDasharray: `0 ${c}` }}
                animate={{ strokeDasharray: `${dash} ${c}` }}
                transition={{ duration: 1, ease: "easeOut" }}
              />
            </g>
          );
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <div className="font-display text-2xl font-light leading-none text-white">{Math.round(completionPct)}%</div>
        <div className="text-[9px] font-mono uppercase tracking-[0.16em] text-[#7C8FA8] mt-0.5">Complete</div>
      </div>
    </div>
  );
}

/* Four routine tiles mapped to real course destinations + a completion ring. */
const ROUTINES = [
  { key: "mobility", label: "Morning Mobility", sub: "Wake the spine", icon: Activity, color: "#22D3EE", to: (s) => `/journey/${s}/daily-practice` },
  { key: "strength", label: "Strength & Rehab", sub: "This week's lesson", icon: Dumbbell, color: "#F59E0B", to: (s) => `/journey/${s}/video` },
  { key: "mindbody", label: "Mind & Body", sub: "Reset the loop", icon: Brain, color: "#34C759", to: (s) => `/journey/${s}` },
  { key: "relax", label: "Evening Relax", sub: "Wind down & journal", icon: Moon, color: "#A855F7", to: (s) => `/journey/${s}/journal` },
];

export default function DailyRoutineGrid({ step = 1, completionPct = 0, weeksDone = 0, activitiesThisWeek = 0 }) {
  const activitiesPct = Math.min(100, (activitiesThisWeek / 7) * 100);
  const weeksPct = (weeksDone / 4) * 100;
  return (
    <div className="mt-6" data-testid="daily-routine">
      <div className="bps-section-head mb-3 text-[#34C759]" style={{ textShadow: "0 0 14px rgba(52,199,89,0.5)" }}>Course Program at a Glance</div>
      <div className="grid grid-cols-2 gap-3">
        {ROUTINES.map(({ key, label, sub, icon: Icon, color, to }, i) => (
          <motion.div
            key={key}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: i * 0.07 }}
          >
            <Link
              to={to(step)}
              data-testid={`routine-${key}`}
              className="group block relative overflow-hidden rounded-3xl p-4 h-full border-2 transition-all hover:-translate-y-0.5 hover:brightness-110"
              style={{
                borderColor: color,
                background: `radial-gradient(130% 110% at 50% 0%, ${color}3d, transparent 65%), linear-gradient(165deg, ${color}26 0%, rgba(8,16,34,0.9) 70%)`,
                boxShadow: `0 0 22px ${color}66, 0 0 0 1px ${color}55, inset 0 0 26px ${color}26, inset 0 1px 0 rgba(255,255,255,0.08)`,
              }}
            >
              <div className="font-display text-sm font-semibold text-white leading-tight drop-shadow-[0_1px_6px_rgba(0,0,0,0.6)]">{label}</div>
              <div className="flex justify-center my-3">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{ background: `${color}33`, border: `1.5px solid ${color}`, boxShadow: `0 0 24px ${color}99, inset 0 0 12px ${color}55` }}
                >
                  <Icon size={26} strokeWidth={2} style={{ color, filter: `drop-shadow(0 0 8px ${color})` }} />
                </div>
              </div>
              <div className="text-[11px] font-medium leading-tight" style={{ color: `${color}` }}>{sub}</div>
            </Link>
          </motion.div>
        ))}
      </div>

      {/* Completion ring tile */}
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="mt-3 relative overflow-hidden rounded-3xl p-5 border border-white/10 flex items-center gap-5"
        style={{
          background: "radial-gradient(120% 100% at 100% 0%, rgba(168,85,247,0.16), transparent 55%), linear-gradient(160deg, rgba(10,21,48,0.85), rgba(3,16,31,0.92))",
          boxShadow: "0 14px 34px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.05)",
        }}
        data-testid="routine-progress-ring"
      >
        <TripleRing completionPct={completionPct} activitiesPct={activitiesPct} weeksPct={weeksPct} />
        <div className="flex-1">
          <div className="bps-section-head text-[#A855F7]" style={{ textShadow: "0 0 14px rgba(168,85,247,0.55)" }}>Course Progress</div>
          <div className="font-display text-lg text-white mt-1">{weeksDone} of 4 weeks</div>
          <div className="mt-2 space-y-1 text-[11px]">
            <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: "#22D3EE", boxShadow: "0 0 6px #22D3EE" }} /><span className="text-[#9AAEC6]">Course complete</span></div>
            <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: "#F59E0B", boxShadow: "0 0 6px #F59E0B" }} /><span className="text-[#9AAEC6]">Weekly activity</span></div>
            <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: "#A855F7", boxShadow: "0 0 6px #A855F7" }} /><span className="text-[#9AAEC6]">Weeks unlocked</span></div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
